import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Button } from '../../Button/Button';
import { Loader } from '../../Loader/Loader';
import { LEFT_PANEL_STYLES, LEFT_PANEL_STYLES_FULL_WIDTH, SCROLLABLE_CONTAINER_STYLES } from '../constants';
import type { ItemRenderProps, LeftPanelConfig } from '../types';

type LeftPanelProps<TItem> = LeftPanelConfig<TItem> & {
  renderItem: (props: ItemRenderProps<TItem>) => React.ReactNode;
  singlePanel?: boolean;
};

export const LeftPanel = <TItem,>({
  emptyStateMessage,
  filters,
  getItemId,
  headerActions,
  items,
  listHeader,
  loading,
  onItemSelect,
  pagination,
  pendoFeature,
  renderItem,
  search,
  selectedIds,
  selectionControl,
  singlePanel,
  title,
  totalCount,
}: LeftPanelProps<TItem>) => {
  const { t } = useTranslation();

  const isEmpty = items.length === 0 && !loading;
  const defaultEmptyMessage = emptyStateMessage ?? t('No results found');

  // In single-panel mode the drawer header already shows the title, so hide the panel title here.
  const showTitle = !singlePanel;
  const showTitleRow = showTitle || !!headerActions;
  const showHeader = showTitleRow || !!search || !!filters;

  return (
    <Box sx={singlePanel ? LEFT_PANEL_STYLES_FULL_WIDTH : LEFT_PANEL_STYLES}>
      {showHeader && (
        <Box sx={{ px: 2, pb: 2 }}>
          {showTitleRow && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {showTitle && (
                <Typography variant="body1Bold" component="h6" data-test="left-panel-title">
                  {title}
                  {totalCount !== undefined && ` [ ${totalCount} ]`}
                </Typography>
              )}

              {headerActions}
            </Box>
          )}

          {search && (
            <Box sx={{ mb: 2 }} data-test="search-container">
              {search}
            </Box>
          )}

          {filters && (
            <Box sx={{ mb: 2 }} data-test="filters-container">
              {filters}
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          ...SCROLLABLE_CONTAINER_STYLES,
          border: '1px solid',
          borderColor: 'divider',
          mx: 2,
        }}
        data-test="available-items-list"
        data-pendo-feature={pendoFeature}
      >
        {listHeader && !isEmpty && (
          <Box
            data-test="left-panel-list-header"
            sx={{
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            {listHeader}
          </Box>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Loader />
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 2,
            }}
          >
            <Typography
              variant="body2"
              data-test="empty-state-message"
              sx={{
                color: 'text.secondary',
              }}
            >
              {defaultEmptyMessage}
            </Typography>
          </Box>
        ) : (
          <>
            {items.map((item, index) => {
              const itemId = getItemId(item);
              const isSelected = selectedIds.has(itemId);

              return (
                <Box key={itemId} data-test={`available-item-${index}`}>
                  {renderItem({ isSelected, item, onSelect: onItemSelect, selectionControl })}
                </Box>
              );
            })}

            {pagination && (pagination.hasMore || pagination.isLoading) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Button
                  data-test="load-more-button"
                  onClick={pagination.onLoadMore}
                  variant="outlined"
                  disabled={pagination.isLoading}
                >
                  {pagination.isLoading ? t('Loading...') : (pagination.loadMoreLabel ?? t('Load More'))}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
